import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { guidesApi, guidesKeys } from '@/features/guides/api/guides.api';
import type { GuideDaySchedule, GuideSchedule } from '@/types';

/** "YYYY-MM" → whole-month range the schedule endpoint accepts (≤ 31 days). */
function monthRange(month: string) {
  const [year, m] = month.split('-').map(Number);
  const lastDay = new Date(Date.UTC(year, m, 0)).getUTCDate();
  return { from: `${month}-01`, to: `${month}-${String(lastDay).padStart(2, '0')}` };
}

/**
 * Guide schedule for every month in `months` ("YYYY-MM"), merged into one day lookup.
 * Kept fresh because other travelers may be booking the same guide right now.
 */
export function useGuideSchedule(profileId: string | undefined, months: string[]) {
  const uniqueMonths = useMemo(() => Array.from(new Set(months.filter(Boolean))).sort(), [months]);

  const queries = useQueries({
    queries: uniqueMonths.map((month) => {
      const params = monthRange(month);
      return {
        queryKey: guidesKeys.schedule(profileId ?? '', params),
        queryFn: () => guidesApi.getSchedule(profileId!, params),
        enabled: Boolean(profileId),
        staleTime: 30 * 1000,
      };
    }),
  });

  const meta: Omit<GuideSchedule, 'days'> | undefined = queries.find((q) => q.data)?.data;
  const dataKey = queries.map((q) => q.dataUpdatedAt).join(',');

  const days = useMemo(() => {
    const map = new Map<string, GuideDaySchedule>();
    for (const query of queries) {
      for (const day of query.data?.days ?? []) map.set(day.date, day);
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `queries` is new every render; dataKey changes only with data
  }, [dataKey]);

  return {
    meta,
    days,
    isLoading: queries.some((q) => q.isLoading),
  };
}
